export function hasRenderedTextLayerText(root: HTMLElement | null) {
  if (!root) return false;
  const textLayer = root.querySelector('.react-pdf__Page__textContent');
  return !!textLayer?.textContent?.trim();
}

export function getRectsIntersectionArea(
  a: DOMRectReadOnly,
  b: DOMRectReadOnly
) {
  const width = Math.max(
    0,
    Math.min(a.right, b.right) - Math.max(a.left, b.left)
  );
  const height = Math.max(
    0,
    Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top)
  );
  return width * height;
}

export function isPdfPageVisibleInViewport(
  viewport: Element,
  pageEl: HTMLElement,
  minVisibleRatio = 0.15
) {
  const viewportRect = viewport.getBoundingClientRect();
  const pageRect = pageEl.getBoundingClientRect();
  const visibleArea = getRectsIntersectionArea(pageRect, viewportRect);
  const pageArea = pageRect.width * pageRect.height;
  if (pageArea <= 0) return false;
  return visibleArea / pageArea >= minVisibleRatio;
}

export function getPdfPageScrollTop(viewport: Element, pageEl: HTMLElement) {
  const viewportRect = viewport.getBoundingClientRect();
  const pageRect = pageEl.getBoundingClientRect();
  let scrollTop = viewport.scrollTop + (pageRect.top - viewportRect.top);

  if (pageRect.height < viewportRect.height) {
    scrollTop -= (viewportRect.height - pageRect.height) / 2;
  }

  const maxScroll = viewport.scrollHeight - viewport.clientHeight;
  return Math.max(0, Math.min(scrollTop, maxScroll));
}

export function isPdfPageRendered(pageRoot?: HTMLElement | null) {
  if (!pageRoot) return false;
  const inner = pageRoot.querySelector('.media_viewer_pdf-inner');
  if (!inner) return false;
  return !!inner.querySelector('.react-pdf__Page__canvas');
}

export function pickMostVisiblePage(
  pages: { page: number }[],
  pageElements: Map<number, HTMLDivElement>,
  viewport: Element
) {
  if (!pages.length) return null;

  const viewportRect = viewport.getBoundingClientRect();
  const { scrollTop, scrollHeight, clientHeight } = viewport;
  const scrollEdgeThreshold = 2;
  const isAtTop = scrollTop <= scrollEdgeThreshold;
  const isAtBottom =
    scrollTop + clientHeight >= scrollHeight - scrollEdgeThreshold;

  const firstPage = pages[0].page;
  const lastPage = pages[pages.length - 1].page;

  if (isAtTop && pageElements.has(firstPage)) {
    return firstPage;
  }

  if (isAtBottom && pageElements.has(lastPage)) {
    return lastPage;
  }

  let bestPage: number | null = null;
  let bestArea = 0;

  pages.forEach(({ page }) => {
    const element = pageElements.get(page);
    if (!element) return;

    const rect = element.getBoundingClientRect();
    const visibleArea = getRectsIntersectionArea(rect, viewportRect);

    if (visibleArea > bestArea) {
      bestArea = visibleArea;
      bestPage = page;
    }
  });

  return bestPage;
}

export function getTwoFingerSpan(touches: TouchList) {
  if (touches.length < 2) return 0;
  const dx = touches[0].clientX - touches[1].clientX;
  const dy = touches[0].clientY - touches[1].clientY;
  return Math.hypot(dx, dy);
}

export function getInitialPdfPageScale(
  originalWidth: number,
  originalHeight: number,
  maxCanvasSize: number,
  maxZoom: number,
  isMobile: boolean
) {
  const maxDimension = Math.max(originalWidth, originalHeight);
  const canvasBudget = isMobile ? maxCanvasSize / maxZoom : maxCanvasSize;
  const scale = canvasBudget / maxDimension;
  return {
    width: originalWidth * scale,
    height: originalHeight * scale
  };
}

export function handleSelectionChange(
  textLayersMap: Map<HTMLDivElement, HTMLDivElement>,
  prevRange: Range | null
): Range | null {
  const selection = document.getSelection();
  const selectedText = selection?.toString?.()?.trim() || '';

  if (!selection || selection.rangeCount === 0 || !selectedText) {
    return null;
  }

  const activeTextLayers = new Set<HTMLDivElement>();

  for (let i = 0; i < selection.rangeCount; i++) {
    const range = selection.getRangeAt(i);
    for (const textLayerDiv of textLayersMap.keys()) {
      if (
        !activeTextLayers.has(textLayerDiv) &&
        range.intersectsNode(textLayerDiv)
      ) {
        activeTextLayers.add(textLayerDiv);
      }
    }
  }

  const range = selection.getRangeAt(0);

  const modifyStart =
    prevRange &&
    (range.compareBoundaryPoints(Range.END_TO_END, prevRange) === 0 ||
      range.compareBoundaryPoints(Range.START_TO_END, prevRange) === 0);

  let anchor: any = modifyStart ? range.startContainer : range.endContainer;
  if (anchor.nodeType === Node.TEXT_NODE) {
    anchor = anchor.parentNode;
  }

  if (!modifyStart && range.endOffset === 0) {
    do {
      while (!anchor.previousSibling) {
        anchor = anchor.parentNode;
      }
      anchor = anchor.previousSibling;
    } while (!anchor.childNodes.length);
  }

  const parentTextLayer = anchor.parentElement?.closest('.textLayer');
  if (!parentTextLayer) return null;

  const endDiv = textLayersMap.get(parentTextLayer as HTMLDivElement);

  if (
    endDiv instanceof HTMLDivElement &&
    parentTextLayer instanceof HTMLDivElement
  ) {
    endDiv.style.width = parentTextLayer.style.width;
    endDiv.style.height = parentTextLayer.style.height;
    anchor.parentElement?.insertBefore(
      endDiv,
      modifyStart ? anchor : anchor.nextSibling
    );
  }
  return range.cloneRange();
}

export type PdfZoomScrollAnchor = {
  page: number;
  ratioX: number;
  ratioY: number;
  viewportOffsetX: number;
  viewportOffsetY: number;
  scale: number;
};

export function capturePdfZoomScrollAnchor(
  viewport: Element,
  pageRefsMap: Map<number, HTMLDivElement>,
  activePageNumber: number,
  prevZoom: number,
  nextZoom: number
): PdfZoomScrollAnchor {
  const viewportRect = viewport.getBoundingClientRect();
  const focalX = viewportRect.left + viewportRect.width / 2;
  const focalY = viewportRect.top + viewportRect.height / 2;

  let anchorPage = activePageNumber;
  let ratioX = 0.5;
  let ratioY = 0.5;
  let foundPage = false;

  pageRefsMap.forEach((pageEl, page) => {
    const rect = pageEl.getBoundingClientRect();
    if (focalY >= rect.top && focalY <= rect.bottom) {
      anchorPage = page;
      ratioX = rect.width ? (focalX - rect.left) / rect.width : 0.5;
      ratioY = rect.height ? (focalY - rect.top) / rect.height : 0.5;
      foundPage = true;
    }
  });

  if (!foundPage) {
    const pageEl = pageRefsMap.get(activePageNumber);
    if (pageEl) {
      const rect = pageEl.getBoundingClientRect();
      ratioX = rect.width ? (focalX - rect.left) / rect.width : 0.5;
      ratioY = rect.height ? (focalY - rect.top) / rect.height : 0.5;
    }
  }

  return {
    page: anchorPage,
    ratioX,
    ratioY,
    viewportOffsetX: focalX - viewportRect.left,
    viewportOffsetY: focalY - viewportRect.top,
    scale: prevZoom > 0 ? nextZoom / prevZoom : 1
  };
}

export function restorePdfZoomScroll(
  viewport: Element,
  pageRefsMap: Map<number, HTMLDivElement>,
  anchor: PdfZoomScrollAnchor
) {
  const maxScrollTop = viewport.scrollHeight - viewport.clientHeight;
  const maxScrollLeft = viewport.scrollWidth - viewport.clientWidth;

  const pageEl = anchor.page ? pageRefsMap.get(anchor.page) : undefined;

  let targetTop: number;
  let targetLeft: number;

  if (pageEl) {
    const viewportRect = viewport.getBoundingClientRect();
    const pageRect = pageEl.getBoundingClientRect();
    targetTop =
      viewport.scrollTop +
      (pageRect.top - viewportRect.top) +
      anchor.ratioY * pageRect.height -
      anchor.viewportOffsetY;
    targetLeft =
      viewport.scrollLeft +
      (pageRect.left - viewportRect.left) +
      anchor.ratioX * pageRect.width -
      anchor.viewportOffsetX;
  } else {
    targetTop =
      (viewport.scrollTop + anchor.viewportOffsetY) * anchor.scale -
      anchor.viewportOffsetY;
    targetLeft =
      (viewport.scrollLeft + anchor.viewportOffsetX) * anchor.scale -
      anchor.viewportOffsetX;
  }

  viewport.scrollTo({
    top: Math.max(0, Math.min(targetTop, maxScrollTop)),
    left: Math.max(0, Math.min(targetLeft, maxScrollLeft)),
    behavior: 'instant'
  });
}
